/**
 * Phase 3B.2 — invoice creation wizard.
 *
 * 4-step state machine: Mieter → Mietverhältnis/Zeitraum → Positionen →
 * Vorschau. On Step-4 submit: POST /api/admin/invoices, navigate to
 * /admin/invoices/{id} on 201, map error.status to inline German message
 * on failure.
 *
 * State, navigation, submit flow all live in this page. Step components
 * are pure presentation + setState helpers.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { createAdminInvoice } from "../../api/adminData";
import useInvoiceCapabilities from "./invoices/useInvoiceCapabilities";
import WizardStepContainer from "./InvoiceWizard/WizardStepContainer";
import Step1Tenant from "./InvoiceWizard/Step1Tenant";
import Step2TenancyPeriod from "./InvoiceWizard/Step2TenancyPeriod";
import Step3Items from "./InvoiceWizard/Step3Items";
import Step4Preview from "./InvoiceWizard/Step4Preview";
import {
  buildSubmitPayload,
  canProceedToNextStep,
  canSubmit,
} from "./InvoiceWizard/validation";

const TOTAL_STEPS = 4;
const STEP_TITLES = {
  1: "Mieter auswählen",
  2: "Mietverhältnis und Zeitraum",
  3: "Positionen erfassen",
  4: "Vorschau und Bestätigung",
};

function initialState() {
  const now = new Date();
  return {
    step: 1,
    tenantSearchQuery: "",
    tenantSearchResults: [],
    tenantSearchLoading: false,
    selectedTenant: null,
    tenancies: null,
    tenanciesLoading: false,
    selectedTenancy: null,
    billingYear: now.getFullYear(),
    billingMonth: now.getMonth() + 1,
    // T2.6.1: optional payment term in days. null = backend default
    // (currently hardcoded 10). When set, backend computes
    // due_date = invoice_date + N. See InvoiceCreateRequest schema.
    paymentTermDays: null,
    items: [
      { type: "rent", description: "", quantity: 1, unit_price_chf: 0 },
    ],
    submitting: false,
    submitError: null,
  };
}

function mapSubmitError(err) {
  const status = err?.status;
  const baseMsg = err?.message || "Unbekannter Fehler.";
  if (status === 409) {
    return {
      code: 409,
      message:
        "Rechnung für diesen Zeitraum existiert bereits. " +
        "Bitte zurück zu Schritt 2 und Zeitraum ändern.",
    };
  }
  if (status === 429) {
    return {
      code: 429,
      message: "Zu viele Versuche. Bitte 1 Minute warten.",
    };
  }
  if (status === 422) {
    return {
      code: 422,
      message: "Validierungsfehler: " + baseMsg,
    };
  }
  if (status === 404) {
    return {
      code: 404,
      message:
        "Mietverhältnis nicht mehr gefunden. Bitte zurück zu Schritt 1.",
    };
  }
  return { code: "other", message: baseMsg };
}

export default function AdminInvoiceCreatePage() {
  const navigate = useNavigate();
  const [state, setState] = useState(initialState);
  // INV-RETIRE.1b: hiding the buttons that lead here is not enough — this route is
  // reachable by typing the URL, from a bookmark, or from a browser-history entry
  // made before the organization switched. Same hook, same fail-closed rule.
  //
  // This is a UX guard, NOT the security boundary: `POST /admin/invoices` refuses
  // the write on its own and would do so even if this check were removed. What it
  // buys is that nobody fills in a four-step wizard to earn a 409 at the end.
  const {
    loading: capabilitiesLoading,
    error: capabilitiesError,
    nativeCreationEnabled,
  } = useInvoiceCapabilities();

  const updateState = (patch) =>
    setState((prev) => ({ ...prev, ...patch }));

  async function handleSubmit() {
    if (!canSubmit(state)) return;
    updateState({ submitting: true, submitError: null });
    try {
      const payload = buildSubmitPayload(state);
      const response = await createAdminInvoice(payload);
      // 201 Created — InvoiceDetailResponse.id is the redirect target.
      navigate(`/admin/invoices/${response.id}`);
    } catch (err) {
      updateState({
        submitting: false,
        submitError: mapSubmitError(err),
      });
    }
  }

  function handleNext() {
    if (state.step === TOTAL_STEPS) {
      handleSubmit();
      return;
    }
    updateState({ step: state.step + 1 });
  }

  function handleBack() {
    if (state.step <= 1) return;
    // Clear submitError when leaving Step 4 backwards.
    updateState({ step: state.step - 1, submitError: null });
  }

  const stepBody =
    state.step === 1 ? (
      <Step1Tenant state={state} updateState={updateState} />
    ) : state.step === 2 ? (
      <Step2TenancyPeriod state={state} updateState={updateState} />
    ) : state.step === 3 ? (
      <Step3Items state={state} updateState={updateState} />
    ) : (
      <Step4Preview state={state} />
    );

  const proceed = canProceedToNextStep(state);
  const submitOk = canSubmit(state);

  return (
    <div
      className="min-h-full content-start bg-[#f8fafc] text-[#0f172a] [color-scheme:light] dark:bg-[#07090f] dark:text-[#eef2ff] dark:[color-scheme:dark]"
      style={{ display: "grid", gap: "24px" }}
    >
      <div>
        <button
          type="button"
          onClick={() => navigate("/admin/invoices")}
          className="mb-2 inline-flex items-center gap-1 text-[12px] text-[#64748b] hover:text-[#0f172a] dark:text-[#6b7a9a] dark:hover:text-[#eef2ff]"
        >
          <ArrowLeft className="h-3 w-3" />
          Zurück zu Rechnungen
        </button>
        <h2 style={{ fontSize: "22px", fontWeight: 700, margin: 0 }}>
          Neue Rechnung erstellen
        </h2>
        <p className="mt-[10px] text-[12px] text-[#64748b] dark:text-[#6b7a9a]">
          Erstellt eine einzelne Rechnung für ein Mietverhältnis mit
          beliebigen Positionen.
        </p>
      </div>

      {!nativeCreationEnabled ? (
        /* An explaining card rather than a redirect. A redirect would bounce the
           operator back to the list with no account of why, and it would make the
           browser-back button oscillate. The way back is offered above; nothing
           here is a dead end.

           Deliberately three different sentences for three different situations:
           still loading, could not ask, and asked-and-the-answer-is-no. Showing
           "deactivated" during a slow request would state something the page does
           not know yet. */
        <div
          data-testid="native-invoice-creation-disabled"
          role="status"
          className="rounded-[14px] border border-black/10 bg-white p-6 dark:border-white/[0.07] dark:bg-[#141824]"
        >
          <h3 className="mb-2 mt-0 text-[14px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
            {capabilitiesLoading
              ? "Berechtigungen werden geprüft …"
              : capabilitiesError
              ? "Berechtigungen konnten nicht geprüft werden"
              : "Rechnungserstellung in Vantio ist deaktiviert"}
          </h3>
          <p className="text-[12px] text-[#64748b] dark:text-[#6b7a9a]">
            {capabilitiesLoading
              ? "Einen Moment, bis feststeht, ob für diese Organisation Rechnungen in Vantio erstellt werden."
              : capabilitiesError
              ? "Solange das unklar ist, wird hier kein Rechnungsformular angeboten. Bestehende Rechnungen bleiben unverändert abrufbar."
              : "Für diese Organisation werden Rechnungen in Bexio erstellt. Bestehende Rechnungen bleiben hier vollständig abrufbar."}
          </p>
        </div>
      ) : (
      <WizardStepContainer
        title={STEP_TITLES[state.step]}
        currentStep={state.step}
        totalSteps={TOTAL_STEPS}
        onBack={state.step > 1 ? handleBack : null}
        onNext={handleNext}
        nextLabel={
          state.step === TOTAL_STEPS ? "Rechnung erstellen" : "Weiter"
        }
        nextDisabled={
          state.step === TOTAL_STEPS ? !submitOk : !proceed
        }
        nextLoading={state.step === TOTAL_STEPS && state.submitting}
      >
        {stepBody}
      </WizardStepContainer>
      )}
    </div>
  );
}
